import { useEffect, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import {
  bannerFor,
  confirmWords,
  waitingFeeds,
  type BannerCheck,
  type BringInStatus,
} from './feedBringInWords';

export const FEED_BRING_IN = gql`
  query FeedBringIn($accountId: ID!) {
    feedBringIn(accountId: $accountId) {
      available
      liveSource
      feeds {
        id
        name
        type
        waiting
        live
        bringInNew
      }
      job {
        id
        status
        dataSourceId
        bringInNew
        total
        done
        brought
        live
        drafts
        flagged
        failed
        failedLive
        failedReasons {
          gone
          noTitle
          error
        }
        checks {
          code
          label
          count
        }
      }
    }
  }
`;

const START_FEED_BRING_IN = gql`
  mutation StartFeedBringIn($accountId: ID!, $dataSourceId: ID, $bringInNew: Boolean) {
    startFeedBringIn(accountId: $accountId, dataSourceId: $dataSourceId, bringInNew: $bringInNew) {
      id
      status
    }
  }
`;

const DISMISS_FEED_BRING_IN = gql`
  mutation DismissFeedBringIn($accountId: ID!, $jobId: ID!) {
    dismissFeedBringIn(accountId: $accountId, jobId: $jobId)
  }
`;

/** What a banner button asks the list to show. */
export type BringInShow =
  | { kind: 'waiting'; feedId: string | null }
  | {
      kind: 'job';
      jobId: string;
      outcome: 'flagged' | 'failed';
      label: string;
      /** One check's articles (flagged): its finding code. */
      check?: string;
    };

/**
 * Content → Articles: the one banner about articles that came from a feed
 * (Feed Articles design D–F). Offers "Bring all in" while feed articles are
 * waiting, shows progress while the job runs (it polls), then what checks
 * found and what couldn't be brought in. The job runs on the server: leaving
 * the page doesn't stop it.
 *
 * askOpen: open the confirm on arrival (the article screen's "Bring all in").
 */
export function FeedBringInBanner({
  accountId,
  askOpen = false,
  onAskHandled,
  onShow,
  showing = false,
  onShowAll,
  onProgress,
}: {
  accountId: string;
  askOpen?: boolean;
  onAskHandled?: () => void;
  onShow: (show: BringInShow) => void;
  /** The list already shows what "Show them" picks: the button clears it (QA round 13). */
  showing?: boolean;
  onShowAll?: () => void;
  /** Articles moved into TinyCMS: the list's tags and counts changed. */
  onProgress?: () => void;
}) {
  const { data, refetch, startPolling, stopPolling } = useQuery<{ feedBringIn: BringInStatus }>(
    FEED_BRING_IN,
    { variables: { accountId }, errorPolicy: 'all', fetchPolicy: 'cache-and-network' }
  );
  const status = data?.feedBringIn ?? null;
  const banner = bannerFor(status);
  const running = banner?.kind === 'running';
  const done = status?.job?.done ?? 0;

  // Poll while it runs; the list refreshes as articles come in.
  useEffect(() => {
    if (!running) return;
    startPolling(2000);
    return () => stopPolling();
  }, [running, startPolling, stopPolling]);
  const [seenDone, setSeenDone] = useState(done);
  useEffect(() => {
    if (done === seenDone) return;
    // Every ~200 articles, and when it stops, not on every poll.
    if (Math.abs(done - seenDone) >= 200 || !running) {
      setSeenDone(done);
      onProgress?.();
    }
  }, [done, seenDone, running, onProgress]);

  const [asking, setAsking] = useState(false);
  const [bringNew, setBringNew] = useState(true);
  const [failure, setFailure] = useState<string | null>(null);
  const [start, { loading: starting }] = useMutation(START_FEED_BRING_IN);
  const [dismiss, { loading: dismissing }] = useMutation(DISMISS_FEED_BRING_IN);

  useEffect(() => {
    if (!askOpen || !status) return;
    if (banner?.kind === 'offer') setAsking(true);
    onAskHandled?.();
  }, [askOpen, status, banner?.kind, onAskHandled]);

  if (!banner || !status) return null;
  const feeds = waitingFeeds(status);

  const bringAll = async () => {
    setFailure(null);
    try {
      await start({
        variables: {
          accountId,
          dataSourceId: feeds.length === 1 ? feeds[0].id : null,
          bringInNew: bringNew,
        },
      });
      setAsking(false);
      await refetch();
    } catch (err) {
      setFailure(err instanceof Error ? err.message : 'It couldn’t start. Try again.');
    }
  };
  const dismissJob = async () => {
    if (!status.job) return;
    await dismiss({ variables: { accountId, jobId: status.job.id } }).catch(() => {});
    await refetch();
  };
  const words = asking ? confirmWords(status) : null;

  return (
    <>
      {banner.kind === 'offer' ? (
        <div className="fb-banner" data-kind="offer">
          <div className="fb-banner-text">
            <span className="fb-banner-title">{banner.title}</span>
            <span className="fb-banner-body">{banner.body}</span>
          </div>
          <button
            type="button"
            className="ink-btn"
            aria-pressed={showing}
            onClick={() =>
              showing && onShowAll
                ? onShowAll()
                : onShow({ kind: 'waiting', feedId: banner.feedId })
            }
          >
            {showing && onShowAll ? 'Show all articles' : 'Show them'}
          </button>
          <button type="button" className="ink-btn ink-btn-primary" onClick={() => setAsking(true)}>
            Bring all in
          </button>
        </div>
      ) : banner.kind === 'running' ? (
        <div className="fb-banner" data-kind="running" role="status" aria-live="polite">
          <div className="fb-banner-row">
            <span className="fb-banner-title">{banner.title}</span>
            <span className="fb-banner-count">{banner.count}</span>
          </div>
          <div
            className="fb-progress"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={banner.percent}
            aria-label="Articles brought in"
          >
            <div style={{ width: `${banner.percent}%` }} />
          </div>
          <span className="fb-banner-note">{banner.body}</span>
        </div>
      ) : banner.kind === 'done' ? (
        <div className="fb-banner" data-kind="done">
          <div className="fb-banner-text">
            <span className="fb-banner-title">
              <span className="fb-banner-tick" aria-hidden="true">
                ✓
              </span>
              {banner.title}
            </span>
            <span className="fb-banner-body">{banner.body}</span>
            <BannerChecks checks={banner.checks} jobId={status.job?.id} onShow={onShow} />
          </div>
          {banner.showFlagged && status.job ? (
            <button
              type="button"
              className="ink-btn ink-btn-primary"
              onClick={() =>
                onShow({
                  kind: 'job',
                  jobId: status.job!.id,
                  outcome: 'flagged',
                  label: banner.checks.length
                    ? 'Checks found things to fix'
                    : 'Checks found something',
                })
              }
            >
              {banner.showFlagged}
            </button>
          ) : null}
          <button
            type="button"
            className="ink-btn ink-btn-quiet"
            disabled={dismissing}
            onClick={() => void dismissJob()}
          >
            Dismiss
          </button>
        </div>
      ) : (
        <div className="fb-banner" data-kind="partial">
          <div className="fb-banner-text">
            <span className="fb-banner-body">{banner.body}</span>
            <BannerChecks checks={banner.checks} jobId={status.job?.id} onShow={onShow} />
          </div>
          {status.job ? (
            <button
              type="button"
              className="ink-btn"
              onClick={() =>
                onShow({
                  kind: 'job',
                  jobId: status.job!.id,
                  outcome: 'failed',
                  label: 'Couldn’t be brought in',
                })
              }
            >
              {banner.showFailed}
            </button>
          ) : null}
          {banner.showFlagged && status.job ? (
            <button
              type="button"
              className="ink-btn"
              onClick={() =>
                onShow({
                  kind: 'job',
                  jobId: status.job!.id,
                  outcome: 'flagged',
                  label: banner.checks.length
                    ? 'Checks found things to fix'
                    : 'Checks found something',
                })
              }
            >
              {banner.showFlagged}
            </button>
          ) : null}
          <button
            type="button"
            className="ink-btn ink-btn-quiet"
            disabled={dismissing}
            onClick={() => void dismissJob()}
          >
            Dismiss
          </button>
        </div>
      )}
      {words ? (
        <div
          className="ink-modal-back"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !starting) setAsking(false);
          }}
        >
          <div
            className="ink-modal fb-confirm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="bring-all-title"
          >
            <div className="ink-modal-title" id="bring-all-title">
              {words.title}
            </div>
            <div className="ink-modal-copy">{words.stays}</div>
            <div className="ink-modal-copy">{words.checks}</div>
            <div className="ink-modal-note">{words.note}</div>
            <label className="fb-confirm-option">
              <input
                type="checkbox"
                checked={bringNew}
                onChange={(e) => setBringNew(e.target.checked)}
              />
              <span>
                {words.option}
                <span className="fb-confirm-note">{words.optionNote}</span>
              </span>
            </label>
            <div className="fb-confirm-note">{words.estimate}</div>
            {failure ? (
              <div className="ink-modal-copy" role="alert" style={{ color: 'var(--ink-red)' }}>
                {failure}
              </div>
            ) : null}
            <div className="ink-modal-actions">
              <button
                type="button"
                className="ink-btn"
                autoFocus
                disabled={starting}
                onClick={() => setAsking(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ink-btn ink-btn-primary"
                disabled={starting}
                onClick={() => void bringAll()}
              >
                {starting ? 'Starting…' : 'Bring all in'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

/** "No author · 2,540", each its own filter, with that check's name on the chip. */
function BannerChecks({
  checks,
  jobId,
  onShow,
}: {
  checks: BannerCheck[];
  jobId?: string;
  onShow: (show: BringInShow) => void;
}) {
  if (!checks.length || !jobId) return null;
  return (
    <span className="fb-banner-checks">
      {checks.map((c) => (
        <button
          key={c.code}
          type="button"
          className="fb-check"
          onClick={() =>
            onShow({ kind: 'job', jobId, outcome: 'flagged', check: c.code, label: c.label })
          }
        >
          {c.label} <span className="fb-check-n">{c.count}</span>
        </button>
      ))}
    </span>
  );
}
